import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { FieldArrayWithId } from 'react-hook-form';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';

type DraggableQuestionListProps = {
  fields: FieldArrayWithId<QuestionnaireFormData, 'questions', '_id'>[];
  onDragEnd: (oldIndex: number, newIndex: number) => void;
  children: React.ReactNode;
};

export const DraggableQuestionList: React.FC<DraggableQuestionListProps> = ({
  fields,
  onDragEnd,
  children,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field._id === active.id);
      const newIndex = fields.findIndex((field) => field._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onDragEnd(oldIndex, newIndex);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={fields.map((field) => field._id)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};
